import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FIELDS } from "@/components/booking-form/registry/fields";
import { SLOTS } from "@/components/booking-form/registry/slots";
import { Plus } from "lucide-react";
import { SlotPills } from "./SlotPills";
import { SlotLabelEditor } from "./SlotLabelEditor";
import { FieldRow } from "./FieldRow";
import { DividerRow } from "./DividerRow";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const FieldHeader = () => (
  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 text-[11px] uppercase tracking-wider text-slate-500 px-2">
    <div className="sm:col-span-3">Key / default</div>
    <div className="sm:col-span-3">Label (override)</div>
    <div className="sm:col-span-2">Move to tab</div>
    <div className="sm:col-span-1">Indent</div>
    <div className="sm:col-span-1">Order</div>
    <div className="sm:col-span-1">Used · Req · Vis</div>
    <div className="sm:col-span-1 text-right">Reset</div>
  </div>
);

// One card per tab: slot toggles, slot labels, then fields + dividers in order.
export const TabFieldsCard = ({ fd, tab: t }) => {
  const pos = (f) => fd.effective("field", f.key, "position", f.position) ?? 0;
  const fields = FIELDS.filter((f) => fd.effective("field", f.key, "tab_key", f.tab) === t.key)
    .sort((a, b) => pos(a) - pos(b));
  const slots = SLOTS.filter((s) => s.tab === t.key);
  const tabSlots = t.isCustom ? [] : slots;
  if (!t.isCustom && !fields.length && !tabSlots.length) return null;

  const items = [
    ...fields.map((f) => ({ kind: "field", key: f.key, item: f, pos: pos(f) })),
    ...fd.dividersForTab(t.key).map((d) => ({ kind: "divider", key: d.key, item: d, pos: d.position ?? 0 })),
  ].sort((a, b) => a.pos - b.pos || a.key.localeCompare(b.key));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Badge variant={t.isCustom ? "outline" : "secondary"}
                 className={t.isCustom ? "border-violet-300 text-violet-700 bg-violet-50" : ""}>
            {fd.effective("tab", t.key, "label", t.label)}{t.isCustom && " (custom)"}
          </Badge>
          <span className="text-xs font-normal text-slate-500">
            {plural(fields.length, "field")}
            {tabSlots.length > 0 && ` · ${plural(tabSlots.length, "slot")}`}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {t.isCustom && fields.length === 0 && (
          <p className="text-xs text-slate-500 italic px-2 py-2" data-testid={`fd-empty-custom-${t.key}`}>
            No fields yet. Move fields into this tab using the &quot;Move to tab&quot; dropdown on any field below.
          </p>
        )}
        <SlotPills fd={fd} tabKey={t.key} slots={slots} />
        <SlotLabelEditor fd={fd} tabKey={t.key} slots={slots} />
        {fields.length > 0 && <FieldHeader />}
        {items.map((it, idx) => {
          const Row = it.kind === "divider" ? DividerRow : FieldRow;
          const props = it.kind === "divider" ? { divider: it.item } : { field: it.item };
          return <Row key={it.kind === "divider" ? `div:${it.key}` : it.key} fd={fd} {...props}
                      isFirst={idx === 0} isLast={idx === items.length - 1} />;
        })}
        <div className="pt-2">
          <Button size="sm" variant="outline" onClick={() => fd.addDivider(t.key)} disabled={!fd.clientId}
                  data-testid={`fd-add-divider-${t.key}`}>
            <Plus className="h-3 w-3 mr-1" /> Add section divider
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
